class WhosAuxSellingPlans extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    const dataNode = this.querySelector('[data-wa-selling-plan-data]');
    if (!dataNode) return;

    try {
      this.data = JSON.parse(dataNode.textContent);
    } catch (error) {
      console.error('[Who\'s AUX] Unable to parse selling-plan data.', error);
      return;
    }

    this.form = this.closest('form');
    this.options = this.querySelector('[data-wa-selling-plan-options]');
    this.status = this.querySelector('[data-wa-selling-plan-status]');
    this.hiddenInput = this.querySelector('[data-wa-selling-plan-input]');
    this.productId = this.dataset.id;
    this.defaultToPlan = this.dataset.defaultToPlan !== 'false';
    this.currentVariantId = String(this.dataset.currentVariant || '');
    this.selectedPlanByVariant = {};

    this.onChange = this.onChange.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);

    this.addEventListener('change', this.onChange);
    window.addEventListener(`theme:product:${this.productId}:variantChanged`, this.onVariantChange);

    this.render(this.currentVariantId);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    if (this.productId) {
      window.removeEventListener(`theme:product:${this.productId}:variantChanged`, this.onVariantChange);
    }
  }

  onVariantChange(event) {
    const variant = event.detail && event.detail.variant;
    if (!variant || !variant.id) return;
    this.currentVariantId = String(variant.id);
    this.render(this.currentVariantId);
  }

  onChange(event) {
    const input = event.target.closest('[data-wa-selling-plan-choice]');
    if (!input) return;

    const value = input.value || '';
    this.hiddenInput.value = value;
    this.selectedPlanByVariant[this.currentVariantId] = value;
    this.updateSelectedState();
  }

  render(variantId) {
    const variant = this.data.variants[String(variantId)];
    if (!variant) {
      this.showUnavailable();
      return;
    }

    const plans = Array.isArray(variant.plans) ? variant.plans : [];
    this.options.innerHTML = '';
    this.status.hidden = true;
    this.status.textContent = '';

    if (!plans.length) {
      if (variant.requiresSellingPlan) {
        this.showUnavailable();
      } else {
        this.renderOneTimeOnly(variant);
      }
      return;
    }

    const previous = this.selectedPlanByVariant[String(variantId)];
    let selectedValue = previous;

    if (selectedValue === undefined || (selectedValue && !plans.some(plan => String(plan.id) === String(selectedValue)))) {
      if (variant.requiresSellingPlan || this.defaultToPlan) {
        selectedValue = String(plans[0].id);
      } else {
        selectedValue = '';
      }
    }

    if (!variant.requiresSellingPlan) {
      this.options.appendChild(this.makeChoice({
        id: '',
        name: this.data.labels.oneTime,
        selected: selectedValue === '',
        meta: ''
      }));
    }

    plans.forEach((plan) => {
      const selected = String(plan.id) === String(selectedValue);
      this.options.appendChild(this.makeChoice({
        id: String(plan.id),
        name: plan.name,
        description: plan.description,
        selected,
        meta: this.planMeta(plan)
      }));
    });

    this.hiddenInput.value = selectedValue || '';
    this.selectedPlanByVariant[String(variantId)] = selectedValue || '';
    this.setPurchaseUnavailable(false, variant.available);
    this.updateSelectedState();
  }

  renderOneTimeOnly(variant) {
    this.options.appendChild(this.makeChoice({
      id: '',
      name: this.data.labels.oneTime,
      selected: true,
      meta: ''
    }));
    this.hiddenInput.value = '';
    this.setPurchaseUnavailable(false, variant.available);
    this.updateSelectedState();
  }

  makeChoice({ id, name, description = '', selected = false, meta = '' }) {
    const label = document.createElement('label');
    label.className = 'wa-selling-plans__choice';

    const input = document.createElement('input');
    input.type = 'radio';
    input.name = `wa_selling_plan_choice_${this.productId}`;
    input.value = id;
    input.checked = selected;
    input.setAttribute('data-wa-selling-plan-choice', '');

    const marker = document.createElement('span');
    marker.className = 'wa-selling-plans__radio';
    marker.setAttribute('aria-hidden', 'true');

    const copy = document.createElement('span');
    copy.className = 'wa-selling-plans__choice-copy';

    const title = document.createElement('span');
    title.className = 'wa-selling-plans__choice-title';
    title.textContent = name;
    copy.appendChild(title);

    if (meta) {
      const metaNode = document.createElement('span');
      metaNode.className = 'wa-selling-plans__choice-meta';
      metaNode.textContent = meta;
      copy.appendChild(metaNode);
    }

    if (description) {
      const descriptionNode = document.createElement('span');
      descriptionNode.className = 'wa-selling-plans__choice-description';
      descriptionNode.textContent = description;
      copy.appendChild(descriptionNode);
    }

    label.append(input, marker, copy);
    return label;
  }

  planMeta(plan) {
    const checkout = Number(plan.checkoutCharge || 0);
    const perDelivery = Number(plan.perDeliveryPrice || 0);
    if (checkout > 0 && perDelivery > 0 && checkout !== perDelivery) {
      return `${this.data.labels.dueToday}: ${plan.checkoutChargeFormatted} · ${plan.perDeliveryFormatted} ${this.data.labels.perDelivery}`;
    }
    if (checkout > 0) {
      return `${this.data.labels.dueToday}: ${plan.checkoutChargeFormatted}`;
    }
    return '';
  }

  updateSelectedState() {
    this.querySelectorAll('.wa-selling-plans__choice').forEach((choice) => {
      const input = choice.querySelector('input');
      choice.toggleAttribute('data-selected', Boolean(input && input.checked));
    });
  }

  showUnavailable() {
    this.options.innerHTML = '';
    this.hiddenInput.value = '';
    this.status.textContent = this.data.labels.unavailable;
    this.status.hidden = false;
    this.setPurchaseUnavailable(true, false);
  }

  setPurchaseUnavailable(unavailable, variantAvailable) {
    if (!this.form) return;
    this.form.toggleAttribute('data-wa-selling-plan-unavailable', unavailable);
    requestAnimationFrame(() => {
      const buttons = this.form.querySelectorAll('.product-buy-buttons--primary');
      buttons.forEach((button) => {
        if (unavailable) {
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
        } else if (variantAvailable) {
          button.disabled = false;
          button.removeAttribute('aria-disabled');
        }
      });
    });
  }
}

if (!customElements.get('product-selling-plans-element')) {
  customElements.define('product-selling-plans-element', WhosAuxSellingPlans);
}
